import React, { useState, useEffect, useRef } from "react";
import "./WordByWordAnimator.css";

const WordByWordAnimator = ({ text, delay = 0 }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);
  const textRef = useRef(null);

  // Split text into tokens: words and whitespace (preserves newlines and spaces)
  const tokens = text.match(/[^\s]+|\s+/g) || [];

  // Count the number of actual words (non-whitespace tokens)
  const numWords = tokens.filter(token => !/\s+/.test(token)).length;

  // Intersection Observer to detect when component is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      {
        threshold: 0.1, // Lowered to 0.1 to ensure triggering even for longer texts on smaller screens
        rootMargin: "0px 0px -50px 0px", // Start a bit before fully in view
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [isInView]);

  useEffect(() => {
    // Reset animation when text changes
    setCurrentWordIndex(0);
    setIsAnimating(false);
  }, [text]);

  useEffect(() => {
    // Only start animation if component is in view
    if (!isInView) return;

    const startTimer = setTimeout(() => {
      setIsAnimating(true);
    }, delay);

    return () => clearTimeout(startTimer);
  }, [delay, isInView]);

  useEffect(() => {
    if (!isAnimating || currentWordIndex >= numWords) {
      return;
    }

    const timer = setTimeout(() => {
      setCurrentWordIndex((prev) => prev + 1);
    }, 50); // 50ms per word for balanced speed

    return () => clearTimeout(timer);
  }, [currentWordIndex, numWords, isAnimating]);

  // Auto-scroll functionality
  useEffect(() => {
    if (!isAnimating || !textRef.current || currentWordIndex === 0) return;

    // Find the scrollable parent container
    const scrollableParent = textRef.current.closest(
      ".letter-content, .qa-content"
    );
    if (!scrollableParent) return;

    // Get the last revealed word (query all .word spans)
    const words = textRef.current.querySelectorAll(".word");
    const lastRevealed = words[currentWordIndex - 1];
    if (!lastRevealed) return;

    const wordRect = lastRevealed.getBoundingClientRect();
    const containerRect = scrollableParent.getBoundingClientRect();

    // Calculate if we need to scroll
    const isWordBelowViewport = wordRect.bottom > containerRect.bottom - 50; // 50px buffer

    if (isWordBelowViewport) {
      // Smooth scroll to keep the current text in view
      const scrollTop = scrollableParent.scrollTop;
      const scrollAmount = wordRect.bottom - containerRect.bottom + 80; // Extra padding

      scrollableParent.scrollTo({
        top: scrollTop + scrollAmount,
        behavior: "smooth",
      });
    }
  }, [currentWordIndex, isAnimating]);

  // Render tokens with words wrapped in spans
  let wordCounter = 0;
  const renderedText = tokens.map((token, index) => {
    if (/\s+/.test(token)) {
      return <React.Fragment key={index}>{token}</React.Fragment>;
    } else {
      const thisWordIndex = wordCounter;
      wordCounter++;
      return (
        <span
          key={index}
          className={`word ${thisWordIndex < currentWordIndex ? "revealed" : "hidden"}`}
        >
          {token}
        </span>
      );
    }
  });

  return (
    <div ref={containerRef} className="word-animator-container">
      <div className="text-container">
        <div ref={textRef} className="animated-text">
          {renderedText}
        </div>
      </div>
    </div>
  );
};

export default WordByWordAnimator;